/**
 * Centralized theme color configuration
 * 
 * This is the single source of truth for all theme colors.
 * Modify colors here to update both the useThemeColors hook and CSS variables.
 */

export const lightThemeColors = {
    // Background colors
    mainBg: '#ffffff',
    secondBg: '#f5f5f5',
    
    // Text colors
    text: '#333',
    secondaryText: '#00000090',
    txtWhite: '#fff',
    
    // Brand colors
    senseColor: '#2196f3',
    primary: '#0059A8',
    primaryOnClick: '#096dd9',
    primaryText: '#0059A8',
    
    // Status colors (MUST match original styles.js for light mode)
    success: '#318500',
    successDark: '#246300', // Darker green for "Very Confident"
    successBg: 'rgba(92, 255, 0, 0.05)',
    warning: '#FFBF00',
    warningText: '#9B6803',
    warningBackground: '#fffbe6',
    warningBg: 'rgba(255, 154, 0, 0.05)',
    error: '#e10008',
    errorBg: 'rgba(163, 25, 30, 0.05)',
    
    // UI colors
    disabled: '#eee',
    info: '#0059A8',
    borderColor: '#d8dee4',
    boxShadow: 'rgba(149, 157, 165, 0.2) 0px 8px 24px',
    nestedCardBg: '#ffffff', // White to match default Card in light mode
    nestedCardBgHover: '#fafafa', // Slightly darker on hover
    groupCardBg: '#ffffff', // Group containers - same as nestedCardBg in light mode
    
    // Interactive state colors (for consistency with dark mode)
    hoverBg: 'rgba(0, 0, 0, 0.04)', // Subtle hover background
    
    // Special context colors (for elements on dark backgrounds in light mode)
    breadcrumbText: '#00000090', // Original secondaryTextColor - for dark header
}

export const darkThemeColors = {
    // Background colors (Jira Dark Theme)
    mainBg: '#1d2125', // Jira/Atlassian dark background
    secondBg: '#22272b', // Jira card/surface color
    
    // Text colors
    text: '#C7D1DB', // Jira primary text
    secondaryText: '#bac3cd', // WCAG AAA compliant on all backgrounds (was #8C9BAB)
    txtWhite: '#ffffff',
    
    // Ant Design token colors (for ConfigProvider)
    // These are used in app.jsx for component theming
    textPrimary: 'rgba(255, 255, 255, 0.85)', // Primary text on dark backgrounds
    textSecondary: 'rgba(255, 255, 255, 0.65)', // Secondary/muted text
    textTertiary: 'rgba(255, 255, 255, 0.45)', // Placeholder text
    textQuaternary: 'rgba(255, 255, 255, 0.25)', // Very muted text
    
    // Interactive state colors
    hoverBg: 'rgba(255, 255, 255, 0.08)', // Subtle hover background
    activeBg: 'rgba(255, 255, 255, 0.1)', // Selected/active background
    activeBgHover: 'rgba(255, 255, 255, 0.15)', // Selected + hover
    
    // Brand colors
    senseColor: '#9bc5ff', // WCAG AAA compliant on all backgrounds (was #579DFF)
    primary: '#1668dc', // Darker blue - works for buttons with white text
    primaryHover: '#3c89e8', // Lighter on hover
    primaryOnClick: '#1554ad',
    primaryText: '#9bc5ff', // Lighter blue for links/text (WCAG AAA compliant)
    
    // Status colors
    success: '#66d5a7', // WCAG AAA compliant (was #4BCE97)
    successDark: '#4CAF85', // Darker green for "Very Confident" - WCAG AAA compliant
    successBg: 'rgba(75, 206, 151, 0.15)',
    warning: '#F5CD47', // Jira warning yellow - already AAA compliant
    warningText: '#F5CD47',
    warningBackground: '#3F3102',
    warningBg: 'rgba(245, 205, 71, 0.15)',
    error: '#fbafaa', // WCAG AAA compliant on all backgrounds (was #F87168)
    errorBg: 'rgba(248, 113, 104, 0.15)',
    
    // UI colors
    disabled: '#BCD6F00A',
    info: '#9bc5ff', // Lighter blue for info text (WCAG AAA compliant)
    borderColor: '#A6C5E229', // Jira subtle border
    boxShadow: '0px 1px 1px #091e424f, 0px 0px 1px #091e424f', // Jira subtle card shadow
    nestedCardBg: '#2c333a', // Distinct from secondBg (#22272b) for nested cards
    nestedCardBgHover: '#323940', // Lighter on hover
    groupCardBg: '#22272b', // Group containers - darker than nestedCardBg so items pop out
    
    // Special context colors
    breadcrumbText: '#ffffff', // White for visibility on dark header
}

/**
 * Get theme colors for a specific mode
 * @param {'light' | 'dark'} mode - Theme mode
 * @returns {Object} Color object for the specified theme
 */
export const getThemeColors = (mode) => {
    return mode === 'dark' ? darkThemeColors : lightThemeColors
}

